import GIS from "./app";
import OlGeolocation, {Options as OlGeolocationOptions} from 'ol/Geolocation';
import Feature from "ol/Feature";
import {Circle, Geometry} from "ol/geom";
import Point from "ol/geom/Point";
import {GeolocationState} from "./general/callback";
import {ProjectionLike, transform} from "ol/proj";
import {Coordinate} from "ol/coordinate";
import {GeolocationOptions} from "./configuration/control";
import {StyleLike} from "ol/style/Style";

class Geolocation {
    private readonly GIS: GIS;

    private POSITION_LAYER: string = 'position';
    private ACCURACY_LAYER: string = 'accuracy';
    private features: {[key:string]:Feature<Geometry>} = {};
    public olObject!: OlGeolocation | undefined;
    private state: boolean = false;
    private projection!: ProjectionLike;
    private onChange:GeolocationState = (state:boolean) => this.state = state

    constructor(GIS: GIS) {
        this.GIS = GIS;
    }
    /**
     * Clear the Geolocation object.
     * Useful, when you need to re-init the map component.
     */
    clear () {
        delete this.features[this.POSITION_LAYER];
        delete this.features[this.ACCURACY_LAYER];
        this.olObject = undefined;
    }
    /**
     * Initialization of the geolocation control.
     * @param config - Geolocation configuration from the control config.
     */
    public init(config: GeolocationOptions) {
        const geolocationOptions:OlGeolocationOptions = {
            trackingOptions: {
                //enableHighAccuracy must be set to true to have the heading value.
                enableHighAccuracy: config.highAccuracy ?? false,
                timeout: config.timeout ?? Infinity,
                maximumAge: config.maximumAge ?? 0
            },
            projection: config.projection
        };

        this.olObject = new OlGeolocation(geolocationOptions);

        this.olObject.on('error', error => {
            this.setState(false);
            console.error('Geolocation: Location unavailable. Error: ' + error.message);
        });

        this.olObject.on('change:position', () => {
            const coordinates = this.olObject?.getPosition();

            const geometry = coordinates ? new Point(coordinates) : null;

            if (geometry) {
                this.setFeatureGeometry(this.POSITION_LAYER, geometry)
            } else {
                this.setFeatureGeometry(this.POSITION_LAYER, new Point([]))
            }
        });

        this.olObject.on('change:accuracyGeometry', () => {
            const accuracyGeometry = this.olObject?.getAccuracyGeometry();
            if (accuracyGeometry) {
                this.setFeatureGeometry(this.ACCURACY_LAYER, accuracyGeometry);
            }
        });

        if (config.enabled) {
            this.setTracking(config.enabled);
        }

        this.projection = config.projection ?? 'EPSG:4326';
    }
    /**
     * Enable tracking of the location from the HTML5 Geolocation object
     * @param state True for the geolocation watcher start (Need to be allowed in a browser)
     */
    public setTracking(state: boolean) {
        this.setState(state);
        this.olObject?.setTracking(state);
    }
    /**
     * Ready state show watcher.
     * @private
     * @param callback
     */
    public setOnStateChange(callback:GeolocationState) {
        this.onChange = callback;
    }

    private setState(state: boolean) {
        this.state = state;
        this.onChange(state);
    }
    /**
     * Add geolocation point manually e.g. from android device.
     * @param coordinates - X,Y or Lon, Lat coordinates
     * @param accuracy - Accuracy in meters
     * @param definedEpsg - [epsg='EPSG:4326'] - Input coordinate system.
     * @param rotation {number} - Rotation in degrees
     */
    public addLocation(coordinates: Coordinate, accuracy: number | undefined, definedEpsg: ProjectionLike, rotation: number) {

        const epsg = !definedEpsg ? 'EPSG:4326' : definedEpsg;
        const targetEpsg = this.projection ? this.projection : 'EPSG:4326';

        const transformedCoordinates = transform(coordinates, epsg, targetEpsg);
        const pointGeometry = coordinates ? new Point(transformedCoordinates) : null;
        const accuracyGeometry = coordinates ? new Circle(transformedCoordinates, accuracy) : null;

        if (pointGeometry) {
            this.setFeatureGeometry(this.POSITION_LAYER, pointGeometry);
        }

        if (accuracyGeometry) {
            this.setFeatureGeometry(this.ACCURACY_LAYER, accuracyGeometry);
        }

        if (rotation) {
            this.setFeatureProperties(this.POSITION_LAYER, {
                geolocationRotationDegrees: rotation
            })
        }
    }
    /**
     * Set geolocation availability style
     * @param status - [status='available'] Location accuracy/availability as a category/style
     * @throws No such style defined.
     */
    public setAvailability(status:string) {
        this.setFeatureStyle(this.POSITION_LAYER, 'position_' + status);
        this.setFeatureStyle(this.ACCURACY_LAYER, 'accuracy_' + status);
    }


    /**
     * Set rotation of the geolocation point
     * @param rotation - Rotation in degrees
     */
    public setRotation(rotation: number) {
        if (rotation) {
            this.setFeatureProperties(this.POSITION_LAYER, {
                geolocationRotationDegrees: rotation
            })
        }
    }

    private createFeature(name:string) {
        const layer = this.GIS.Layer.getLayer(name);
        const source = layer.getSource();

        this.features[name] = new Feature({
            name: name
        }) as Feature<Geometry>;

        source.addFeature(this.features[name])
    }

    private setFeatureGeometry(name: string, geometry:Geometry) {
        if (!this.features[name]) {
            this.createFeature(name)
        }

        this.features[name].setGeometry(geometry);
    }

    private setFeatureProperties(name: string, properties: {[property: string]: any}) {
        if (this.features[name]) {
            this.features[name].setProperties(properties);
        }
    }

    private setFeatureStyle(name: string, styleName: string) {
        const style = this.GIS.Style.getStyle(styleName) as StyleLike | undefined;
        this.features[name].setStyle(style);
    }
    /**
     *
     * @returns Geolocation running
     */
    public getState() {
        return this.state;
    }
}

export default Geolocation;